import styles from "./Checkout.module.css"
import { useState, useEffect, useMemo, useCallback } from 'react';
import Navbar from "../../components/Navbar/NavbarMain/Navbar";
import Footer from "../../components/Footer/Footer";
import { formatCurrency } from "../../utils/prices";
import ProgressBar from "../../components/ProgressBar/ProgressBar";
import { useCartContext } from "../../context/CartContext";
import { usePreferences } from "../../context/PreferencesContext";
import { czk_eur_rate } from "../../constants/prices";
import Step1 from "./components/Steps/Step1/Step1";
import Step2 from "./components/Steps/Step2/Step2";
import Step4 from "./components/Steps/Step4/Step4";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useWindowSize } from "../../hooks/useWindowSize";
import BackButton from "../../components/Button/BackButton/BackButton";
import { groupCartData, getCartPriceSummary, getCartPriceSummaryCurrency, getGroupedBlockCount, getSvozBlockCount, hasTransportForItems } from "../../utils/cart";
import useEnsureCatalogData from "../../hooks/useEnsureCatalogData";
import { log } from "../../utils/logger";
import PageLayout from "../../components/Layout/PageLayout/PageLayout";
/**
* Checkout page.
* Contains the entire checkout and handles checkout step logic.
* Contains summary of the order.
* @returns TSX.Element
*/
const CheckoutPage = () => {
    const { cartData, reloadCart } = useCartContext();
    const { t } = useTranslation(); // Translation
    const navigate = useNavigate();
    const screenWidth = useWindowSize(); // Window width
    const transportPrice = 385; //Sample transport price
    const { selectedCurrency } = usePreferences(); // Current selected currency
    const [step, setStep] = useState(1); // Current setup step
    const [actionsEl, setActionsEl] = useState<HTMLElement | null>(null);
    const actionsRef = useCallback((node: HTMLElement | null) => setActionsEl(node), []);
    const stepNumber = 3; // Number of steps
    const { allProducts, packages, items } = useEnsureCatalogData({
        productSource: "allProducts",
    });

    useEffect(()=>{
        reloadCart();
    },[])
    useEffect(()=>{
        goToTopOfProduct();
        
    },[step])

    // Scroll to top on page change
    const goToTopOfProduct = () => {
        document.getElementById('CheckoutSteps')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // Send user home if his cart is empty
    useEffect(() => {
        if (!cartData) return;
        /*
        if( Object.keys(cartData.items).length === 0 || cartData.totals.total_price_without_tax < minOrderPrice){
            //navigate("/home")
        }
        */
    }, [cartData])
    // Steps
    const stepComponents = {
        1: Step1, // Review of items
        2: Step2, // Complete order form
        3: Step4, // Recap
    };
    const StepComponent = stepComponents[step as keyof typeof stepComponents];
    const groupedCart = groupCartData(cartData ?? null) 
    const groupedBlocksCount = getGroupedBlockCount(groupedCart);


    // Handle transport - svoz
    const transportEx = useMemo(() => {
        if (!cartData?.items) return false;
        return hasTransportForItems(cartData.items, {
            sales: allProducts,
            packages,
            storage: items,
        });
    }, [cartData?.items, items, allProducts, packages]);

    const priceSummary = useMemo(
        () => getCartPriceSummary(cartData, groupedBlocksCount, getSvozBlockCount(groupedCart, { sales: allProducts, packages, storage: items })),
        [cartData, groupedBlocksCount, groupedCart, allProducts, packages, items]
    );
    const priceSummaryCurrency = useMemo(
        () => getCartPriceSummaryCurrency(priceSummary, selectedCurrency, czk_eur_rate),
        [priceSummary, selectedCurrency]
    );

    return(
        <PageLayout
            pageClassName={styles.Page}
            headerClassName={styles.Header}
            headerInnerClassName={styles.Headergroup}
            contentClassName={styles.Content}
            footerClassName={styles.Footer}
            header={<Navbar />}
            footer={<Footer />}
        >
            <div className={styles.CheckoutPage}>
                    <div className={styles.CheckoutHeader}>
                        <BackButton text={t("buttons.back_home")} onClick={() => navigate("/")} />
                        <p className={styles.CheckoutTitle}>{t("checkout.title")}</p>
                    </div>
                    <div className={styles.CheckoutBody}>
                        <div className={styles.Checkout}>
                            <div className={styles.CheckoutSteps} id="CheckoutSteps">
                                {/*
                                
                                
                                <BackButton text={t("buttons.back")} onClick={() => handleStepBack()} />
                                */}
                                <ProgressBar currentStep={step} stepNumber={stepNumber} size="small"/>
                            </div>
                            {/*Current step*/}
                            {StepComponent && <StepComponent onContinue={()=>setStep(step+1)} onBack={()=>setStep(step-1)} actionsContainer={actionsEl}/>}
                        </div>
                        <div className={`${styles.CheckoutBill} ${ screenWidth > 1300 ? styles.CheckoutBillWide : ""}`}>
                            {/*
                            <div className={styles.CheckoutBillInfo}>
                                <div className={styles.InfoGroupSmall}>
                                    <p>{t("checkout.order_summary.price_without_dph")}</p>
                                    <span>{formatCurrency(priceSummaryCurrency.baseNoTax, selectedCurrency)}</span>
                                </div>
                                {transportEx && (
                                <div className={styles.InfoGroupSmall}>
                                    <p>{t("cart.summary_section.collection")}</p>
                                    <span>{formatCurrency(priceSummaryCurrency.collection, selectedCurrency)}</span>
                                </div>
                                )}
                                <div className={styles.InfoGroup}>
                                    <p className={styles.InfoGroupTitle}>{t("checkout.order_summary.subtotal")}</p>
                                    <div className={styles.InfoGroupPrice}>
                                        <p>{t("checkout.order_summary.price_without_dph")}</p>
                                        <span>{formatCurrency(priceSummaryCurrency.totalNoTax, selectedCurrency)}</span>
                                    </div>
                                </div>
                            </div>
                            */}
                            <div className={styles.CheckoutBillSummary}>
                                <p className={styles.CheckoutBillSummaryTitle}>{t("checkout.order_summary.total")}</p>
                                <div className={styles.CheckoutBillSummaryContent}>
                                    <div className={styles.SummaryWithoutDPH}>
                                        <p>{t("checkout.order_summary.price_without_dph")}</p>
                                        <span>{formatCurrency(priceSummaryCurrency.totalNoTax, selectedCurrency)}</span>
                                    </div>
                                    <div className={styles.SummaryWithDPH}>
                                        <p>{t("checkout.order_summary.price_with_dph")}</p>
                                        <span>{formatCurrency(priceSummaryCurrency.totalWithTax, selectedCurrency)}</span>
                                    </div>
                                </div>
                            </div>
                            <div ref={actionsRef} className={styles.BillActions} />
                        </div>
                    </div>
                </div>
        </PageLayout>
    )
}

export default CheckoutPage;
